import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Loader2, Car, AlertCircle, RefreshCw } from 'lucide-react';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Clé API OpenRouteService
const ORS_API_KEY = 'eyJvcmciOiI1YjNjZTM1OTc4NTExMTAwMDFjZjYyNDgiLCJpZCI6IjI3Nzg3NDhlOTMyNDRhNTliYWExNjNmNDJmNDYyOTk3IiwiaCI6Im11cm11cjY0In0=';

// Configuration des zones de distance routière (en mètres)
// Couleurs bien distinctes et contrastées pour une meilleure lisibilité
const DISTANCE_ZONES = [
  { distance: 20000, label: '20 km', fee: 'Sans surplus', color: '#16a34a', fillColor: '#22c55e' },   // Vert vif
  { distance: 40000, label: '40 km', fee: '+10€', color: '#ca8a04', fillColor: '#fbbf24' },           // Jaune/Or
  { distance: 60000, label: '60 km', fee: '+20€', color: '#ea580c', fillColor: '#fb923c' },           // Orange
  { distance: 80000, label: '80 km', fee: '+30€', color: '#dc2626', fillColor: '#f87171' },           // Rouge
  { distance: 100000, label: '100 km', fee: '+40€', color: '#7c3aed', fillColor: '#a78bfa' }          // Violet
];

// Composant pour ajuster la vue de la carte aux isochrones
const FitBoundsToIsochrones = ({ isochrones }) => {
  const map = useMap();
  
  useEffect(() => {
    if (isochrones && isochrones.length > 0) {
      // Trouver l'isochrone la plus grande (dernière)
      const largestIsochrone = isochrones[isochrones.length - 1];
      if (largestIsochrone && largestIsochrone.geometry) {
        try {
          const geoJsonLayer = L.geoJSON(largestIsochrone);
          const bounds = geoJsonLayer.getBounds();
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [20, 20] });
          }
        } catch (e) {
          console.error('Erreur ajustement vue:', e);
        }
      }
    }
  }, [isochrones, map]);
  
  return null;
};

const InteractiveMapDriving = () => {
  // Coordonnées exactes de 140 Avenue Montaigne, Saint-Médard-en-Jalles
  const center = [44.8925, -0.7089];
  
  const [isochrones, setIsochrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const fetchedRef = useRef(false);

  // Fonction pour récupérer les isochrones depuis OpenRouteService
  const fetchIsochrones = async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    
    setLoading(true);
    setError(null);
    setLoadingProgress('Connexion à OpenRouteService...');
    
    try {
      // Construire la requête pour les isochrones de distance
      // L'API ORS utilise le paramètre "range" pour la distance en mètres
      // et "range_type": "distance" pour spécifier que c'est une distance (pas un temps)
      const ranges = DISTANCE_ZONES.map(z => z.distance);
      
      setLoadingProgress('Calcul des zones de distance routière...');
      
      const response = await fetch('https://api.openrouteservice.org/v2/isochrones/driving-car', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': ORS_API_KEY
        },
        body: JSON.stringify({
          locations: [[center[1], center[0]]], // [longitude, latitude] - ORS utilise [lng, lat]
          range: ranges,
          range_type: 'distance', // IMPORTANT: distance en mètres, pas temps
          units: 'm',
          attributes: ['total_pop'],
          smoothing: 25
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Erreur API: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        // Créer des zones en forme d'anneaux (sans superposition)
        // Chaque zone = polygone externe - polygone interne (trou)
        const ringIsochrones = [];
        
        for (let i = 0; i < data.features.length; i++) {
          const outerFeature = data.features[i];
          const innerFeature = i > 0 ? data.features[i - 1] : null;
          const zoneConfig = DISTANCE_ZONES[i] || DISTANCE_ZONES[0];
          
          let ringGeometry;
          
          if (innerFeature && outerFeature.geometry.type === 'Polygon' && innerFeature.geometry.type === 'Polygon') {
            // Créer un anneau : polygone externe avec le polygone interne comme trou
            ringGeometry = {
              type: 'Polygon',
              coordinates: [
                outerFeature.geometry.coordinates[0], // Contour externe
                innerFeature.geometry.coordinates[0]  // Trou (contour interne inversé)
              ]
            };
          } else {
            // Première zone ou géométrie non-polygone : garder tel quel
            ringGeometry = outerFeature.geometry;
          }
          
          ringIsochrones.push({
            type: 'Feature',
            geometry: ringGeometry,
            properties: {
              ...outerFeature.properties,
              zoneConfig,
              isRing: !!innerFeature
            }
          });
        }
        
        setIsochrones(ringIsochrones);
        setLoadingProgress('');
      } else {
        throw new Error('Aucune zone isochrone reçue');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des isochrones:', err);
      setError(err.message || 'Impossible de charger les zones de distance routière');
      // Réinitialiser le ref pour permettre un nouvel essai
      fetchedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIsochrones();
  }, []);

  // Fonction de retry
  const handleRetry = () => {
    fetchedRef.current = false;
    fetchIsochrones();
  };

  // Style pour chaque isochrone GeoJSON
  // Zones en anneaux avec remplissage et contours en pointillés
  const getIsochroneStyle = (feature) => {
    const zoneConfig = feature.properties?.zoneConfig;
    
    return {
      color: zoneConfig?.color || '#8B9E8A',
      weight: 3,
      opacity: 1,
      fillColor: zoneConfig?.fillColor || '#8B9E8A',
      fillOpacity: 0.25, // Remplissage visible pour chaque zone
      dashArray: '8, 6', // Contours en pointillés
      lineCap: 'round',
      lineJoin: 'round'
    };
  };

  // Popup pour chaque isochrone
  const onEachFeature = (feature, layer) => {
    const zoneConfig = feature.properties?.zoneConfig;
    if (zoneConfig) {
      layer.bindPopup(`
        <div style="text-align: center; padding: 8px;">
          <strong style="color: ${zoneConfig.color}; font-size: 18px;">${zoneConfig.label}</strong>
          <br/>
          <span style="color: #666; font-size: 14px;">Distance routière</span>
          <br/>
          <span style="color: #333; font-size: 16px; font-weight: bold;">${zoneConfig.fee}</span>
          <br/>
          <span style="color: #888; font-size: 11px;">Depuis Saint-Médard-en-Jalles</span>
        </div>
      `);
      
      // Tooltip au survol
      layer.bindTooltip(`<strong>${zoneConfig.label}</strong> - ${zoneConfig.fee}`, {
        permanent: false,
        direction: 'top'
      });
    }
  };

  return (
    <div className="relative w-full h-[600px] rounded-xl overflow-hidden shadow-xl border-2 border-[#C5A572]/30">
      {/* Indicateur de chargement */}
      {loading && (
        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-[1001] flex flex-col items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#8B9E8A] mb-4" />
          <p className="text-[#8B9E8A] font-semibold text-lg">Calcul des zones de distance...</p>
          {loadingProgress && (
            <p className="text-gray-500 text-sm mt-2">{loadingProgress}</p>
          )}
          <p className="text-gray-400 text-xs mt-4">
            <Car className="inline h-4 w-4 mr-1" />
            Distances routières réelles (pas à vol d'oiseau)
          </p>
        </div>
      )}

      {/* Message d'erreur */}
      {error && !loading && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-[1001] flex flex-col items-center justify-center p-4">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-red-600 font-semibold text-lg text-center mb-2">Erreur de chargement</p>
          <p className="text-gray-500 text-sm text-center mb-4 max-w-md">{error}</p>
          <button 
            onClick={handleRetry}
            className="flex items-center gap-2 px-4 py-2 bg-[#8B9E8A] text-white rounded-lg hover:bg-[#7A8A77] transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </button>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={9}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Afficher les isochrones du plus grand au plus petit */}
        {isochrones.slice().reverse().map((isochrone, index) => (
          <GeoJSON
            key={`isochrone-${index}`}
            data={isochrone}
            style={getIsochroneStyle}
            onEachFeature={onEachFeature}
          />
        ))}

        {/* Ajuster la vue aux isochrones */}
        <FitBoundsToIsochrones isochrones={isochrones} />

        {/* Marqueur pour Saint-Médard-en-Jalles */}
        <Marker position={center}>
          <Popup>
            <div className="text-center">
              <strong className="text-[#8B9E8A] text-lg">Gratia Animalis</strong>
              <br />
              <span className="text-gray-600">Saint-Médard-en-Jalles</span>
            </div>
          </Popup>
        </Marker>
      </MapContainer>

      {/* Légende */}
      <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-4 border border-[#C5A572]/30 z-[1000]">
        <h4 className="font-bold text-[#8B9E8A] mb-3 text-sm flex items-center gap-2">
          <Car className="h-4 w-4" />
          Zones de déplacement
        </h4>
        <p className="text-[10px] text-gray-500 mb-3 italic">
          Distance routière réelle (pas à vol d'oiseau)
        </p>
        <div className="space-y-2 text-xs">
          {DISTANCE_ZONES.map((zone, index) => (
            <div key={index} className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full border-2"
                style={{ 
                  backgroundColor: zone.color,
                  borderColor: zone.color,
                  opacity: 0.9
                }}
              ></div>
              <span className="text-gray-700">
                <strong>{zone.label}</strong> - {zone.fee}
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
          Depuis Saint-Médard-en-Jalles
        </p>
      </div>

      {/* Badge indicateur du type de calcul */}
      <div className="absolute top-4 left-4 bg-[#8B9E8A] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-[1000] flex items-center gap-1.5">
        <Car className="h-3.5 w-3.5" />
        Distance routière en km
      </div>
    </div>
  );
};

export default InteractiveMapDriving;
